import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLanguage } from "context/LanguageContext";
import { useNotification } from "context/NotificationContext";
import { api } from "services/api";

export interface SelfFaceCheckResponse {
  face_check_id: string | null;
  matched: boolean;
  user_id: string | null;
  full_name: string | null;
  similarity_score: number | null;
  message: string | null;
  attendance: {
    id: string;
    session_id: string;
    status: string | null;
    checked_in_at: string | null;
    checked_out_at: string | null;
    check_in_method: string | null;
  } | null;
}

async function selfFaceCheck(
  file: Blob,
  sessionId?: string,
  location?: string
): Promise<SelfFaceCheckResponse> {
  const form = new FormData();
  form.append("file", file, "capture.jpg");
  if (sessionId) form.append("session_id", sessionId);
  if (location) form.append("location", location);
  return api.upload<SelfFaceCheckResponse>("/users/me/face/check", form);
}

/**
 * Self-service face check-in/out camera flow, shared by the standalone
 * `/user/self-checkin` page and the embedded flow inside
 * `SessionDetailModal` (opening a session's detail no longer navigates
 * away to a separate screen — the camera renders inline instead).
 */
export default function FaceCheckinPanel({
  sessionId,
  onResult,
}: {
  sessionId?: string;
  onResult?: (result: SelfFaceCheckResponse) => void;
}) {
  const { t } = useLanguage();
  const { addNotification } = useNotification();

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [cameraOn, setCameraOn] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<SelfFaceCheckResponse | null>(null);

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: 640, height: 480 },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraOn(true);
      setCapturedImage(null);
      setResult(null);
    } catch {
      addNotification(t("selfCheckin.cameraError"), "error");
    }
  }, [addNotification, t]);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((tr) => tr.stop());
    streamRef.current = null;
    setCameraOn(false);
  }, []);

  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach((tr) => tr.stop());
    };
  }, []);

  const capture = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    setCapturedImage(dataUrl);
    stopCamera();
  }, [stopCamera]);

  const submit = useCallback(async () => {
    if (!capturedImage) return;
    setSubmitting(true);
    setResult(null);
    try {
      const resp = await fetch(capturedImage);
      const blob = await resp.blob();

      let location: string | undefined;
      try {
        const pos = await new Promise<GeolocationPosition>((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            timeout: 5000,
          })
        );
        location = `${pos.coords.latitude},${pos.coords.longitude}`;
      } catch {
        // location is optional
      }

      const res = await selfFaceCheck(blob, sessionId || undefined, location);
      setResult(res);
      onResult?.(res);

      if (res.matched && res.attendance) {
        const status = res.attendance.status;
        if (status === "checked_in") {
          addNotification(t("selfCheckin.checkedIn"), "success");
        } else if (status === "checked_out") {
          addNotification(t("selfCheckin.checkedOut"), "success");
        } else {
          addNotification(t("selfCheckin.matchSuccess"), "success");
        }
      } else if (res.matched) {
        addNotification(t("selfCheckin.matchSuccess"), "success");
      } else {
        addNotification(t("selfCheckin.noMatch"), "warning");
      }
    } catch (err: any) {
      addNotification(err.message || t("selfCheckin.error"), "error");
    } finally {
      setSubmitting(false);
    }
  }, [capturedImage, sessionId, addNotification, t, onResult]);

  const reset = useCallback(() => {
    setCapturedImage(null);
    setResult(null);
  }, []);

  return (
    <div>
      {/* Camera area */}
      <div className="relative mb-4 overflow-hidden rounded-xl bg-surface-muted">
        {/* Always mounted (never conditionally rendered) so `videoRef` is
            already attached to the DOM element by the time `startCamera()`
            assigns `srcObject` to it — otherwise the ref is still null on
            the first render after `setCameraOn(true)` and the stream never
            attaches, leaving a black frame. */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={`w-full rounded-xl ${
            cameraOn && !capturedImage ? "" : "hidden"
          }`}
        />
        {capturedImage && (
          <img
            src={capturedImage}
            alt="captured"
            className="w-full rounded-xl"
          />
        )}
        {!cameraOn && !capturedImage && (
          <div className="flex h-64 items-center justify-center text-ink-subtle">
            {t("selfCheckin.cameraOff")}
          </div>
        )}
      </div>

      <canvas ref={canvasRef} className="hidden" />

      {/* Action buttons */}
      <div className="mb-4 flex gap-3">
        {!cameraOn && !capturedImage && (
          <button
            onClick={startCamera}
            className="flex-1 rounded-lg bg-brand-500 px-4 py-2 text-white hover:bg-brand-600"
          >
            {t("selfCheckin.openCamera")}
          </button>
        )}
        {cameraOn && !capturedImage && (
          <>
            <button
              onClick={capture}
              className="flex-1 rounded-lg bg-brand-500 px-4 py-2 text-white hover:bg-brand-600"
            >
              {t("selfCheckin.capture")}
            </button>
            <button
              onClick={stopCamera}
              className="rounded-lg bg-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-400 dark:bg-surface-hover dark:text-white"
            >
              {t("selfCheckin.cancel")}
            </button>
          </>
        )}
        {capturedImage && (
          <>
            <button
              onClick={submit}
              disabled={submitting}
              className="flex-1 rounded-lg bg-green-500 px-4 py-2 text-white hover:bg-green-600 disabled:opacity-50"
            >
              {submitting
                ? t("selfCheckin.submitting")
                : t("selfCheckin.submit")}
            </button>
            <button
              onClick={reset}
              disabled={submitting}
              className="rounded-lg bg-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-400 dark:bg-surface-hover dark:text-white"
            >
              {t("selfCheckin.retake")}
            </button>
          </>
        )}
      </div>

      {/* Result */}
      {result && (
        <div
          className={`rounded-xl p-4 ${
            result.matched
              ? "bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-300"
              : "bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-300"
          }`}
        >
          <p className="font-semibold">
            {result.matched
              ? t("selfCheckin.resultMatched")
              : t("selfCheckin.resultNotMatched")}
          </p>
          {result.similarity_score != null && (
            <p className="text-sm">
              {t("selfCheckin.score")}:{" "}
              {(result.similarity_score * 100).toFixed(1)}%
            </p>
          )}
          {result.message && <p className="text-sm">{result.message}</p>}
          {result.attendance && (
            <div className="border-current/20 mt-2 border-t pt-2 text-sm">
              <p className="font-medium">{t("selfCheckin.attendanceStatus")}</p>
              <p>
                {result.attendance.status === "checked_in"
                  ? t("selfCheckin.statusCheckedIn")
                  : result.attendance.status === "checked_out"
                  ? t("selfCheckin.statusCheckedOut")
                  : result.attendance.status}
              </p>
              {result.attendance.checked_in_at && (
                <p>
                  {t("selfCheckin.checkedInAt")}:{" "}
                  {new Date(
                    result.attendance.checked_in_at
                  ).toLocaleTimeString()}
                </p>
              )}
              {result.attendance.checked_out_at && (
                <p>
                  {t("selfCheckin.checkedOutAt")}:{" "}
                  {new Date(
                    result.attendance.checked_out_at
                  ).toLocaleTimeString()}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
